import { useState, useCallback, useEffect } from 'react';

const MARGIN = 5; // extra buffer space around windows
const STEP_SIZE = 200; // distance between steps in the spiral

function doesOverlap(x, y, w, h, windowStates) {
  for (const state of Object.values(windowStates)) {
    if (!state.position || !state.size) {
      // If position or size is not defined yet, skip this window
      continue;
    }

    const wx = state.position.x;
    const wy = state.position.y;
    const ww = state.size.width;
    const wh = state.size.height;

    const xOverlap = (x + w + MARGIN > wx - MARGIN) && (x - MARGIN < wx + ww + MARGIN);
    const yOverlap = (y + h + MARGIN > wy - MARGIN) && (y - MARGIN < wy + wh + MARGIN);
    if (xOverlap && yOverlap) return true;
  }
  return false;
}


function findFreeSpiralPosition(w, h, windowStates) {
  let x = 0, y = 0;
  let step = 1;
  let direction = 0; // 0: right, 1: down, 2: left, 3: up
  let segmentLengthCount = 0;
  let segmentChange = 0;

  for (let i = 0; i < 10000; i++) {
    // Check if current spot is free
    if (!doesOverlap(x, y, w, h, windowStates)) {
      return { x, y };
    }

    // Move in the spiral pattern
    switch (direction) {
      case 0: x += STEP_SIZE; break;
      case 1: y += STEP_SIZE; break;
      case 2: x -= STEP_SIZE; break;
      case 3: y -= STEP_SIZE; break;
    }
    segmentLengthCount++;

    if (segmentLengthCount === step) {
      segmentLengthCount = 0;
      direction = (direction + 1) % 4;
      segmentChange++;
      if (segmentChange === 2) {
        segmentChange = 0;
        step++;
      }
    }
  }

  // fallback
  return { x: 100, y: 100 };
}

export const useWindowManager = (canvasRef) => {
  const [activeApps, setActiveApps] = useState([]);
  const [windowStates, setWindowStates] = useState({});
  const [focusedWindowId, setFocusedWindowId] = useState(null);

  const bringWindowToFront = useCallback((windowId) => {
    setFocusedWindowId(windowId);
    setWindowStates(prev => {
      const maxZ = Math.max(0, ...Object.values(prev).map(w => w.zIndex || 0));
      return {
        ...prev,
        [windowId]: {
          ...prev[windowId],
          zIndex: maxZ + 1
        }
      };
    });
  }, []);

  const initializeWindow = useCallback((windowId, metadata = {}) => {
    const w = metadata.width || 800;
    const h = metadata.height || 600;

    const { x, y } = findFreeSpiralPosition(w, h, windowStates);

    setWindowStates(prev => ({
      ...prev,
      [windowId]: {
        isMinimized: false,
        position: { x, y },
        size: { width: w, height: h },
        zIndex: Math.max(0, ...Object.values(prev).map(w => w.zIndex || 0)) + 1
      }
    }));

    return { x, y, width: w, height: h };
  }, [windowStates]);

  const closeWindow = useCallback((windowId) => {
    setActiveApps(prev => prev.filter(app => app.id !== windowId));
    setWindowStates(prev => {
      const { [windowId]: removed, ...rest } = prev;
      return rest;
    });
    if (focusedWindowId === windowId) {
      const remainingWindows = Object.entries(windowStates)
        .filter(([id]) => id !== windowId)
        .sort((a, b) => (b[1].zIndex || 0) - (a[1].zIndex || 0));
      
      if (remainingWindows.length > 0) {
        setFocusedWindowId(remainingWindows[0][0]);
      } else {
        setFocusedWindowId(null);
      }
    }
  }, [focusedWindowId, windowStates]);

  const toggleMinimize = useCallback((windowId) => {
    setWindowStates(prev => ({
      ...prev,
      [windowId]: {
        ...prev[windowId],
        isMinimized: !prev[windowId].isMinimized
      }
    }));
  }, []);

  const updateWindowPosition = useCallback((windowId, position) => {
    setWindowStates(prev => ({
      ...prev,
      [windowId]: {
        ...prev[windowId],
        position
      }
    }));
  }, []);

  const updateWindowSize = useCallback((windowId, size) => {
    setWindowStates(prev => ({
      ...prev,
      [windowId]: {
        ...prev[windowId],
        size
      }
    }));
  }, []);

  const launchApp = useCallback((app) => {
    if (app.type === 'web') {
      window.open(app.url, '_blank');
    } else if (app.type === 'internal') {
      setActiveApps(prev => {
        const existing = prev.find(a => a.id === app.id);
        if (!existing) {
          // Initialize window state
          initializeWindow(app.id, app.metadata || {});
          // Return new apps array with app added
          const newApps = [...prev, app];
          // Bring to front after state update
          setTimeout(() => bringWindowToFront(app.id), 0);
          return newApps;
        } else {
          // App exists, bring to front
          setTimeout(() => {
            setWindowStates(currentStates => {
              const state = currentStates[app.id];
              if (state?.isMinimized) {
                toggleMinimize(app.id);
              } else {
                bringWindowToFront(app.id);
              }
              return currentStates;
            });
          }, 0);
          return prev;
        }
      });
    }
    return null;
  }, [initializeWindow, bringWindowToFront, toggleMinimize]);

  return {
    activeApps,
    windowStates,
    focusedWindowId,
    launchApp,
    closeWindow,
    toggleMinimize,
    updateWindowPosition,
    updateWindowSize,
    bringWindowToFront
  };
};
